// src/common/Button.jsx
export default function Button({
  children,
  variant = "primary",
  size = "md",
  block = false,
  as: Comp = "button",
  href,
  to,
  disabled,
  className = "",
  style,
  ...rest
}) {
  // `focus-visible` rather than `focus` for the ring: a mouse click focuses a
  // button, so `focus:ring-*` drew a ring that looked like a stray border and sat
  // there until you clicked elsewhere. `focus-visible` fires only when the browser
  // judges the focus worth showing — keyboard and Tab navigation — so the ring
  // still does its accessibility job without appearing on every click.
  const baseStyles =
    "inline-flex items-center justify-center font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";

  // Variant styles
  const variantStyles = {
    primary:
      "bg-palette-indigo-badge text-white hover:bg-palette-indigo-heading focus-visible:ring-palette-indigo-label",
    secondary:
      "bg-palette-gray-secondary text-white hover:bg-palette-gray-badge focus-visible:ring-palette-gray-secondary",
    success:
      "bg-palette-green-badge text-white hover:bg-palette-green-hover focus-visible:ring-palette-green-solid",
    "gradient-success":
      "bg-gradient-to-r from-palette-emerald-indicator to-palette-emerald-label text-white hover:from-palette-emerald-label hover:to-palette-emerald-badge focus-visible:ring-palette-emerald-label",
    danger:
      "bg-palette-red-badge text-white hover:bg-palette-red-heading focus-visible:ring-palette-red-label",
    outline:
      "border-2 border-palette-indigo-badge text-palette-indigo-badge hover:bg-palette-indigo-soft focus-visible:ring-palette-indigo-label",
    ghost:
      "text-palette-gray-heading hover:bg-palette-gray-subtle focus-visible:ring-palette-gray-secondary",
    gray: "bg-palette-gray-border text-palette-gray-heading hover:bg-palette-gray-placeholder focus-visible:ring-palette-gray-placeholder",
    link: "bg-transparent text-palette-indigo-badge hover:text-palette-indigo-heading hover:underline focus-visible:ring-palette-indigo-label shadow-none",
  };

  // Size styles (link variant uses minimal sizing: no padding/rounded)
  const sizeStyles = {
    xs: "px-2 py-0.5 text-xs rounded",
    sm: "px-3 py-1.5 text-sm rounded-md",
    md: "px-6 py-2.5 text-base rounded-lg",
    lg: "px-8 py-3 text-lg rounded-lg",
    link: "p-0 min-w-0 rounded-none",
  };

  const effectiveSize = variant === "link" ? "link" : size;

  // Block style
  const blockStyle = block ? "w-full" : "";

  const cls = [
    baseStyles,
    variantStyles[variant] || variantStyles.primary,
    sizeStyles[effectiveSize] || sizeStyles.md,
    blockStyle,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const props = { className: cls, style, disabled, ...rest };
  if (href) props.href = href;
  if (to) props.to = to;

  return <Comp {...props}>{children}</Comp>;
}
